// src/pages/admin/components/OnboardingLadder.jsx
//
// Founder-only onboarding ladder card for /admin/users/:uid. Renders the
// 7-stage ladder from the cached users/{uid}.onboarding block written by
// adminComputeOnboardingStages. Read-only; the nudge action lives in
// NudgeCard. See features/onboarding-recovery/ (AC2, N16).
//
// Stage semantics (mirror of the server): `stage` is the first INCOMPLETE
// step over {1,3,4,5,6,7}; email verification (2) is display-only and never
// affects the number.

import React from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

const STEPS = [
  { n: 1, label: 'Account created' },
  { n: 2, label: 'Email verified', displayOnly: true },
  { n: 3, label: 'Team created' },
  { n: 4, label: 'Season created' },
  { n: 5, label: 'Link shared (first registration started)' },
  { n: 6, label: 'First parent registered' },
  { n: 7, label: 'First message sent' },
];

function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return Number(ts) || 0;
}

function daysAgo(ms) {
  if (!ms) return null;
  return Math.max(0, Math.floor((Date.now() - ms) / 86400000));
}

export default function OnboardingLadder({ onboarding }) {
  if (!onboarding?.stage) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-5">
        <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500 mb-2">Onboarding</h2>
        <p className="text-sm text-zinc-600">
          No stage computed yet. Run <span className="font-semibold">Refresh stages</span> on
          the users list to populate the ladder.
        </p>
      </div>
    );
  }

  const { stage, activated, emailVerified, since, dataWarning } = onboarding;
  const stuckDays = daysAgo(toMs(since));

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500">Onboarding</h2>
        {activated ? (
          <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-lime-100 text-lime-800 border-lime-200">
            ✅ Activated
          </span>
        ) : (
          <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-amber-100 text-amber-800 border-amber-200">
            Stuck at {stage}{stuckDays != null ? ` · ${stuckDays}d` : ''}
          </span>
        )}
      </div>

      {dataWarning && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-1.5">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-px" />
          Stage derivation hit unexpected data on this user. Treat the ladder as approximate.
        </div>
      )}

      <ol className="space-y-1.5">
        {STEPS.map((s) => {
          // Completed: every progression step below the current stage, or all
          // of them when activated. Step 2 completes off emailVerified alone.
          const done = s.displayOnly
            ? !!emailVerified
            : activated || s.n < stage;
          const current = !activated && !s.displayOnly && s.n === stage;
          return (
            <li key={s.n} className="flex items-center gap-2 text-sm">
              {done ? (
                <CheckCircleIcon className="h-4.5 w-4.5 h-5 w-5 text-lime-500 shrink-0" />
              ) : (
                <span className={`h-5 w-5 shrink-0 rounded-full border-2 ${current ? 'border-amber-400 bg-amber-50' : 'border-zinc-200'}`} />
              )}
              <span className={
                current ? 'font-semibold text-amber-800'
                  : done ? 'text-zinc-700'
                  : 'text-zinc-400'
              }>
                {s.n}. {s.label}
                {s.displayOnly && !done && (
                  <span className="ml-1 text-[11px] text-zinc-400">(badge only, not required)</span>
                )}
                {current && stuckDays != null && (
                  <span className="ml-2 text-[11px] font-normal text-amber-700">{stuckDays} days here</span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
